import { configureStore, createSlice, createAsyncThunk, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { fetchAllEstates, fetchEstateById, fetchAllRegions, fetchRegionById, fetchSubRegionsByParentId } from '../api/api';
import authReducer from './authSlice';
import favoritesReducer from './favoritesSlice';
import savedSearchesReducer from './savedSearchesSlice';

const createGenericSlice = (name, initialState) => {
    const hasSubRegions = name === 'regions';

    return createSlice({
        name,
        initialState: {
            list: [],
            current: null,
            loading: false,
            error: null,
            ...(hasSubRegions && { subRegions: [] }),
            ...initialState
        },
        reducers: {
            clearError: (state) => {
                state.error = null;
            },
            clearData: (state) => {
                state.current = null;
                state.error = null;
            }
        },
        extraReducers: (builder) => {
            builder
                .addCase(`${name}/fetchAll/pending`, (state) => {
                    state.loading = true;
                })
                .addCase(`${name}/fetchAll/fulfilled`, (state, action) => {
                    state.list = action.payload;
                    state.loading = false;
                })
                .addCase(`${name}/fetchAll/rejected`, (state, action) => {
                    state.error = action.error.message;
                    state.loading = false;
                })
                .addCase(`${name}/fetchById/pending`, (state) => {
                    state.loading = true;
                })
                .addCase(`${name}/fetchById/fulfilled`, (state, action) => {
                    state.current = action.payload;
                    state.loading = false;
                    state.error = null;
                })
                .addCase(`${name}/fetchById/rejected`, (state, action) => {
                    state.error = action.error.message;
                    state.loading = false;
                });

            if (hasSubRegions) {
                builder
                    .addCase(`${name}/fetchSubRegions/pending`, (state) => {
                        state.loading = true;
                    })
                    .addCase(`${name}/fetchSubRegions/fulfilled`, (state, action) => {
                        state.subRegions = action.payload;
                        state.loading = false;
                    })
                    .addCase(`${name}/fetchSubRegions/rejected`, (state, action) => {
                        state.error = action.error.message;
                        state.loading = false;
                    });
            }
        }
    });
};

const createFetchAllThunk = (name, fetchFunction, hasParams = false) =>
    createAsyncThunk(
        `${name}/fetchAll`,
        async (params) => {
            const { data } = hasParams ? await fetchFunction(params) : await fetchFunction();
            return data;
        }
    );

const createFetchByIdThunk = (name, fetchFunction) =>
    createAsyncThunk(`${name}/fetchById`, async (id) => {
        const { data } = await fetchFunction(id);
        return data;
    });

const createFetchSubRegionsThunk = (name, fetchFunction) =>
    createAsyncThunk(
        `${name}/fetchSubRegions`,
        async (id) => {
            const { data } = await fetchFunction(id);
            return data;
        }
    );

const estatesSlice = createGenericSlice('estates');
const regionsSlice = createGenericSlice('regions');

const rootReducer = combineReducers({
    estates: estatesSlice.reducer,
    regions: regionsSlice.reducer,
    auth: authReducer,
    favorites: favoritesReducer,
    savedSearches: savedSearchesReducer
});

const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['auth']
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const fetchEstates = createFetchAllThunk('estates', fetchAllEstates);
export const fetchEstateByIdThunk = createFetchByIdThunk('estates', fetchEstateById);
export const fetchRegions = createFetchAllThunk('regions', fetchAllRegions);
export const fetchRegionByIdThunk = createFetchByIdThunk('regions', fetchRegionById);
export const fetchSubRegions = createFetchSubRegionsThunk('regions', fetchSubRegionsByParentId);

// Export actions
export const { clearError: clearRegionError, clearData: clearRegionData } = regionsSlice.actions;
export const { clearError: clearEstateError, clearData: clearEstateData } = estatesSlice.actions;

export const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        })
});

export const persistor = persistStore(store);